import React, { useState } from "react";
import { Check, Upload } from "lucide-react";

/**
 * CustomFieldsRenderer — public-facing dynamic form renderer for an
 * admin-configured list of custom fields.
 *
 * Used in:
 *   • LMS RegisterPage.js (LMS batch.registration_form_fields)
 *   • Compete CompeteEventPage.js (event.custom_fields)
 *
 * Field shape matches `CustomFieldsEditor.js`. Renderer is a controlled
 * component — parent owns the `values` dict and receives `onChange(key, val)`
 * for every keystroke. Order is honored from the array (already sorted by
 * the editor) so admins control "above which / below which" placement.
 *
 * Theming: the LMS surfaces have a dark glassmorphic palette; the Compete
 * registration page uses the lighter portal palette. We let the parent pass
 * `theme="dark"` (default) or `theme="light"` so we don't need separate
 * components.
 *
 * `validate()` returns the first missing required field or null. Parent
 * calls this on submit before posting.
 */
export function renderCustomFields({
  fields = [],
  values = {},
  onChange,
  theme = "dark",
  idPrefix = "cf",
}) {
  if (!fields || fields.length === 0) return null;
  const isDark = theme === "dark";
  // Sort defensively so the original `order` is honored even if a stale
  // unsorted array is passed in.
  const ordered = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {ordered.map((f, idx) => (
        <FieldShell
          key={f.key || idx}
          field={f}
          isDark={isDark}
          full={f.type === "textarea" || f.type === "multi_select" || f.type === "file" || f.type === "checkbox"}
        >
          <FieldInput
            field={f}
            value={values[f.key]}
            onChange={(v) => onChange(f.key, v)}
            isDark={isDark}
            testId={`${idPrefix}-input-${f.key || idx}`}
          />
        </FieldShell>
      ))}
    </div>
  );
}

/** Returns the first missing required field's label, or null when valid. */
export function validateCustomFields(fields = [], values = {}) {
  for (const f of fields) {
    if (!f.required) continue;
    const v = values?.[f.key];
    if (f.type === "checkbox") {
      if (!v) return f.label || f.key;
    } else if (f.type === "multi_select") {
      if (!Array.isArray(v) || v.length === 0) return f.label || f.key;
    } else if (v === undefined || v === null || String(v).trim() === "") {
      return f.label || f.key;
    }
  }
  return null;
}

function FieldShell({ field, isDark, full, children }) {
  const labelCls = isDark ? "lms-label" : "portal-label";
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className={`${labelCls} flex items-center gap-1.5`}>
        {field.label || field.key}
        {field.required && <span className="text-[#ff9bb0] text-[11px]">*</span>}
      </label>
      {children}
      {field.help && (
        <div
          className={`mt-1.5 text-[11px] leading-relaxed ${
            isDark ? "text-white/40" : ""
          }`}
          style={isDark ? {} : { color: "var(--px-ink-mute)" }}
        >
          {field.help}
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange, isDark, testId }) {
  const inputCls = isDark ? "lms-input" : "portal-input";
  const checkboxCls = isDark
    ? "flex items-center gap-2.5 cursor-pointer p-3 rounded-[12px] border border-white/10 bg-white/[0.02] hover:border-white/20 transition"
    : "flex items-center gap-2.5 cursor-pointer p-3 rounded-[12px] border";
  switch (field.type) {
    case "textarea":
      return (
        <textarea
          className={inputCls}
          rows={3}
          placeholder={field.placeholder || ""}
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    case "number":
    case "email":
    case "tel":
    case "url":
    case "date":
      return (
        <input
          type={field.type}
          className={inputCls}
          placeholder={field.placeholder || ""}
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    case "select":
      return (
        <select
          className={inputCls}
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        >
          <option value="">{field.placeholder || "— Select —"}</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
    case "multi_select": {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="flex flex-wrap gap-2" data-testid={testId}>
          {(field.options || []).map((o) => {
            const active = arr.includes(o);
            return (
              <button
                type="button"
                key={o}
                onClick={() =>
                  onChange(active ? arr.filter((x) => x !== o) : [...arr, o])
                }
                className={`px-3 py-1.5 rounded-full border text-xs transition ${
                  active
                    ? isDark
                      ? "bg-[rgba(0,229,255,0.1)] border-[rgba(0,229,255,0.5)] text-[#5ceeff]"
                      : "bg-black/5 border-black/30"
                    : isDark
                    ? "bg-white/[0.02] border-white/10 text-white/70 hover:border-white/20"
                    : "border"
                }`}
                style={
                  !isDark && !active
                    ? { borderColor: "var(--px-line)" }
                    : undefined
                }
              >
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    case "checkbox":
      return (
        <label className={checkboxCls} data-testid={testId}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span className={isDark ? "text-sm text-white/85" : "text-sm"}>
            {field.placeholder || "Yes"}
          </span>
        </label>
      );
    case "file":
      return (
        <FileInputBase64
          value={value}
          onChange={onChange}
          isDark={isDark}
          testId={testId}
          placeholder={field.placeholder || "Drop or tap to upload"}
        />
      );
    case "text":
    default:
      return (
        <input
          type="text"
          className={inputCls}
          placeholder={field.placeholder || ""}
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
  }
}

function FileInputBase64({ value, onChange, isDark, testId, placeholder }) {
  const [busy, setBusy] = useState(false);
  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
      setBusy(false);
    };
    reader.onerror = () => {
      setBusy(false);
      // eslint-disable-next-line no-alert
      alert("Upload failed");
    };
    reader.readAsDataURL(f);
  };
  return (
    <label className="block cursor-pointer" data-testid={testId}>
      <input
        type="file"
        accept="image/*,application/pdf"
        onChange={handle}
        className="hidden"
      />
      <div
        className={`flex items-center gap-3 px-4 py-4 rounded-[14px] border-2 border-dashed transition ${
          value
            ? isDark
              ? "border-[rgba(45,232,165,0.4)] bg-[rgba(45,232,165,0.04)]"
              : "border"
            : isDark
            ? "border-white/15 hover:border-[rgba(0,229,255,0.3)] bg-white/[0.02]"
            : "border"
        }`}
        style={!isDark ? { borderColor: "var(--px-line)" } : undefined}
      >
        {value ? (
          <Check size={16} className={isDark ? "text-[#7df5c8]" : ""} />
        ) : (
          <Upload size={16} className={isDark ? "text-white/50" : ""} />
        )}
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm truncate ${isDark ? "text-white/85" : ""}`}
          >
            {busy ? "Uploading…" : value ? "Uploaded — click to change" : placeholder}
          </div>
        </div>
      </div>
    </label>
  );
}

export default renderCustomFields;
